import fs from 'fs-extra';
import path from 'path';
import os from 'os';
import { CONFIG_FILE_NAME } from '../config/defaults.js';
import { DirectoryStatus } from '../types/index.js';

/**
 * 获取配置文件路径
 */
export function getConfigPath(): string {
  return path.join(os.homedir(), CONFIG_FILE_NAME);
}

/**
 * 检查目录状态
 */
export async function checkDirectoryStatus(
  dir: string
): Promise<DirectoryStatus> {
  const exists = await fs.pathExists(dir);

  if (!exists) {
    return DirectoryStatus.NOT_EXISTS;
  }

  const files = await fs.readdir(dir);
  const hasContent = files.length > 0;

  return hasContent ? DirectoryStatus.HAS_CONTENT : DirectoryStatus.EMPTY;
}

/**
 * 检查文件是否存在
 */
export async function fileExists(filePath: string): Promise<boolean> {
  return await fs.pathExists(filePath);
}

/**
 * 读取JSON文件
 */
export async function readJson<T>(filePath: string): Promise<T | null> {
  try {
    const exists = await fileExists(filePath);
    if (!exists) {
      return null;
    }
    return await fs.readJson(filePath);
  } catch (error) {
    return null;
  }
}

/**
 * 写入JSON文件
 */
export async function writeJson(filePath: string, data: any): Promise<void> {
  await fs.ensureFile(filePath);
  await fs.writeJson(filePath, data, { spaces: 2 });
}

/**
 * 递归处理模板文件（变量替换）
 */
export async function processTemplateFiles(
  dir: string,
  answers: Record<string, any>,
  filters?: Record<string, string>
): Promise<void> {
  const Handlebars = (await import('handlebars')).default;

  async function processDir(currentDir: string) {
    const files = await fs.readdir(currentDir);

    for (const file of files) {
      const filePath = path.join(currentDir, file);
      const stat = await fs.stat(filePath);

      // 检查是否需要过滤此文件
      const relativePath = path.relative(dir, filePath).replace(/\\/g, '/');
      if (filters && shouldFilter(relativePath, filters, answers)) {
        await fs.remove(filePath);
        continue;
      }

      if (stat.isDirectory()) {
        await processDir(filePath);
      } else if (stat.isFile()) {
        // 处理文件内容
        const content = await fs.readFile(filePath, 'utf-8');
        try {
          const template = Handlebars.compile(content);
          const result = template(answers);
          await fs.writeFile(filePath, result, 'utf-8');
        } catch (error) {
          // 如果不是模板文件，跳过
        }

        // 处理文件名（如果包含变量）
        if (file.includes('{{') || file.includes('__')) {
          const newFileName = file
            .replace(/{{(\w+)}}/g, (_, key) => answers[key] || '')
            .replace(/__(\w+)__/g, (_, key) => answers[key] || '');
          if (newFileName !== file) {
            const newFilePath = path.join(currentDir, newFileName);
            await fs.rename(filePath, newFilePath);
          }
        }
      }
    }
  }

  await processDir(dir);
}

/**
 * 判断文件是否应该被过滤
 */
function shouldFilter(
  filePath: string,
  filters: Record<string, string>,
  answers: Record<string, any>
): boolean {
  for (const [pattern, condition] of Object.entries(filters)) {
    if (matchPattern(filePath, pattern)) {
      try {
        // 简单的条件判断
        const result = evalCondition(condition, answers);
        if (!result) {
          return true;
        }
      } catch (error) {
        // 条件解析失败，保留文件
      }
    }
  }
  return false;
}

/**
 * 匹配文件路径模式
 */
function matchPattern(filePath: string, pattern: string): boolean {
  const regexPattern = pattern
    .replace(/\*\*/g, '.*')
    .replace(/\*/g, '[^/]*')
    .replace(/\?/g, '.');
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(filePath);
}

/**
 * 简单的条件表达式求值
 */
function evalCondition(condition: string, answers: Record<string, any>): boolean {
  try {
    // 替换变量
    let expr = condition;
    for (const [key, value] of Object.entries(answers)) {
      const regex = new RegExp(`answers\\.${key}`, 'g');
      expr = expr.replace(regex, JSON.stringify(value));
    }
    // 使用 Function 构造器安全地执行表达式
    return new Function(`return ${expr}`)();
  } catch (error) {
    return true;
  }
}

